import React, { useEffect, useState, useCallback } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Trash, Copy, CheckSquare, Square, Upload, DownloadSimple, Clock } from '@phosphor-icons/react';

const Stores = () => {
  const [stores, setStores] = useState([]);
  const [groups, setGroups] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkData, setBulkData] = useState({ playlist_id: '', group_id: '' });
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    region: '',
    group_id: '',
    open_time: '10:00',
    close_time: '22:00'
  });

  const fetchData = useCallback(async () => {
    try {
      const [storesRes, groupsRes, playlistsRes] = await Promise.all([
        apiClient.get('/stores'),
        apiClient.get('/store-groups'),
        apiClient.get('/playlists')
      ]);
      setStores(storesRes.data);
      setGroups(groupsRes.data);
      setPlaylists(playlistsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/stores', formData);
      toast.success('Store created successfully');
      setOpen(false);
      setFormData({ name: '', location: '', region: '', group_id: '', open_time: '10:00', close_time: '22:00' });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const updateHours = async (id, field, value) => {
    if (!value) return;
    try {
      await apiClient.patch(`/stores/${id}`, { [field]: value });
      toast.success('Open hours updated');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this store?')) return;
    try {
      await apiClient.delete(`/stores/${id}`);
      toast.success('Store deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const copyKioskUrl = (storeId) => {
    const publicBase = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${publicBase.replace(/\/$/, '')}/kiosk/${storeId}`;
    navigator.clipboard.writeText(url);
    toast.success('Kiosk URL copied to clipboard');
  };

  // Bulk export: if some stores are selected, export just those; otherwise
  // export every store. Format = CSV so it opens cleanly in Excel/Sheets.
  // Columns kept human-readable so an operator can hand the file to install
  // techs or paste rows straight into a WhatsApp / email thread.
  const exportKioskUrls = () => {
    const publicBase = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, '');
    const rows = (selectedStores.length > 0 ? stores.filter(s => selectedStores.includes(s.id)) : stores);
    if (rows.length === 0) { toast.error('No stores to export'); return; }
    const groupNameFor = (gid) => groups.find(g => g.id === gid)?.name || '';
    // Simple CSV serialiser — wrap every field in quotes and escape any
    // embedded quotes. Handles commas, newlines, and Unicode store names.
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Store Name', 'Group', 'Kiosk URL'];
    const body = rows.map(s => [s.name, groupNameFor(s.group_id), `${publicBase}/kiosk/${s.id}`].map(esc).join(','));
    // Prepend a UTF-8 BOM so Excel opens Unicode names correctly on Windows.
    const csv = '\ufeff' + [header.map(esc).join(','), ...body].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `musicsync-kiosk-urls-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} kiosk URL${rows.length === 1 ? '' : 's'}`);
  };

  const copyAllKioskUrls = () => {
    const publicBase = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, '');
    const rows = (selectedStores.length > 0 ? stores.filter(s => selectedStores.includes(s.id)) : stores);
    if (rows.length === 0) { toast.error('No stores to copy'); return; }
    const text = rows.map(s => `${s.name}\t${publicBase}/kiosk/${s.id}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${rows.length} kiosk URL${rows.length === 1 ? '' : 's'} to clipboard`);
  };

  const toggleStoreSelection = (storeId) => {
    setSelectedStores(prev =>
      prev.includes(storeId) ? prev.filter(id => id !== storeId) : [...prev, storeId]
    );
  };

  const toggleAllStores = () => {
    if (selectedStores.length === stores.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(stores.map(s => s.id));
    }
  };

  const handleBulkOperation = async () => {
    if (selectedStores.length === 0) {
      toast.error('Please select at least one store');
      return;
    }

    try {
      if (bulkAction === 'assign_group') {
        await apiClient.post('/stores/bulk-update', {
          store_ids: selectedStores,
          action: 'assign_group',
          data: { group_id: bulkData.group_id }
        });
        toast.success(`Updated ${selectedStores.length} stores with new group`);
      } else if (bulkAction === 'push_playlist') {
        if (!bulkData.playlist_id) {
          toast.error('Please select a playlist');
          return;
        }
        // Push the selected playlist to every selected store. The backend
        // PATCH endpoint handles the active_playlist_id + broadcasts a
        // schedule_changed WS event so the kiosks pick it up instantly.
        const results = await Promise.allSettled(
          selectedStores.map((sid) =>
            apiClient.patch(`/stores/${sid}`, { active_playlist_id: bulkData.playlist_id })
          )
        );
        const pushed = results.filter((r) => r.status === 'fulfilled').length;
        const failed = results.length - pushed;
        if (failed === 0) {
          toast.success(`Playlist pushed to ${pushed} store${pushed === 1 ? '' : 's'}`);
        } else {
          toast.error(`Pushed to ${pushed} of ${results.length} stores — ${failed} failed`);
        }
      } else if (bulkAction === 'delete') {
        if (!window.confirm(`Delete ${selectedStores.length} stores?`)) return;
        await apiClient.post('/stores/bulk-update', {
          store_ids: selectedStores,
          action: 'delete',
          data: {}
        });
        toast.success(`Deleted ${selectedStores.length} stores`);
      }
      
      setBulkOpen(false);
      setSelectedStores([]);
      setBulkAction('');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="stores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="stores-title">Stores</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Manage Store Outlets</p>
        </div>
        <div className="flex gap-2">
          {selectedStores.length > 0 && (
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white border-0">
                  <Upload size={20} className="mr-2" />
                  Bulk Actions ({selectedStores.length})
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                <DialogHeader>
                  <DialogTitle className="font-mono">Bulk Operations</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Action</Label>
                    <Select value={bulkAction} onValueChange={setBulkAction}>
                      <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]">
                        <SelectValue placeholder="Select action" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                        <SelectItem value="assign_group">Assign to Group</SelectItem>
                        <SelectItem value="push_playlist">Push Playlist</SelectItem>
                        <SelectItem value="delete">Bulk Delete</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {bulkAction === 'assign_group' && (
                    <div>
                      <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Group</Label>
                      <Select value={bulkData.group_id} onValueChange={(val) => setBulkData({...bulkData, group_id: val})}>
                        <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]">
                          <SelectValue placeholder="Select group" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                          {groups.map(group => (
                            <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {bulkAction === 'push_playlist' && (
                    <div>
                      <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Playlist</Label>
                      <Select value={bulkData.playlist_id} onValueChange={(val) => setBulkData({...bulkData, playlist_id: val})}>
                        <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]">
                          <SelectValue placeholder="Select playlist" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                          {playlists.map(playlist => (
                            <SelectItem key={playlist.id} value={playlist.id}>{playlist.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Button onClick={handleBulkOperation} className="w-full bg-purple-600 hover:bg-purple-700 text-white border-0">
                    Apply to {selectedStores.length} Stores
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          <Button
            onClick={copyAllKioskUrls}
            disabled={stores.length === 0}
            className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] disabled:opacity-40"
            data-testid="copy-kiosk-urls-button"
            title={selectedStores.length > 0 ? `Copy ${selectedStores.length} selected URL(s) to clipboard` : 'Copy all kiosk URLs to clipboard'}
          >
            <Copy size={16} className="mr-2" />
            {selectedStores.length > 0 ? `Copy (${selectedStores.length})` : 'Copy URLs'}
          </Button>
          <Button
            onClick={exportKioskUrls}
            disabled={stores.length === 0}
            className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] disabled:opacity-40"
            data-testid="export-kiosk-urls-button"
            title={selectedStores.length > 0 ? `Export ${selectedStores.length} selected as CSV` : 'Export all kiosk URLs as CSV'}
          >
            <DownloadSimple size={16} className="mr-2" />
            {selectedStores.length > 0 ? `Export (${selectedStores.length}) CSV` : 'Export URLs (CSV)'}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="add-store-button">
                <Plus size={20} className="mr-2" />
                Add Store
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
              <DialogHeader>
                <DialogTitle className="font-mono">Add New Store</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-store-form">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Store Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                    required
                    data-testid="store-name-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                    required
                    data-testid="store-location-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Region</Label>
                  <Input
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                    data-testid="store-region-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Group (Optional)</Label>
                  <Select value={formData.group_id} onValueChange={(value) => setFormData({ ...formData, group_id: value })}>
                    <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="store-group-select">
                      <SelectValue placeholder="Select group" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                      {groups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Open Hours</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="time"
                      value={formData.open_time}
                      onChange={(e) => setFormData({ ...formData, open_time: e.target.value })}
                      className="bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                      data-testid="store-open-time-input"
                    />
                    <span className="text-[#8a8477] font-mono">–</span>
                    <Input
                      type="time"
                      value={formData.close_time}
                      onChange={(e) => setFormData({ ...formData, close_time: e.target.value })}
                      className="bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                      data-testid="store-close-time-input"
                    />
                  </div>
                  <p className="text-[10px] text-[#a39e92] font-mono mt-1">Used for the daily Uptime score on the dashboard</p>
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="submit-store-button">
                  Create Store
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {stores.length > 0 && (
        <div className="flex items-center gap-2 bg-white border border-[#c5d4c8] rounded-md p-4">
          <Button
            onClick={toggleAllStores}
            className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border-0"
          >
            {selectedStores.length === stores.length ? <CheckSquare size={20} /> : <Square size={20} />}
            <span className="ml-2">{selectedStores.length === stores.length ? 'Deselect' : 'Select'} All</span>
          </Button>
          <p className="text-sm text-[#8a8477] font-mono">
            {selectedStores.length} of {stores.length} selected
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map((store) => (
          <div
            key={store.id}
            className={`bg-white border rounded-md p-6 transition-all duration-200 hover:-translate-y-1 ${
              selectedStores.includes(store.id) ? 'border-blue-500 bg-blue-900/10' : 'border-[#c5d4c8] hover:border-[#c5d4c8]'
            }`}
            data-testid={`store-card-${store.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 flex-1">
                <Checkbox
                  checked={selectedStores.includes(store.id)}
                  onCheckedChange={() => toggleStoreSelection(store.id)}
                  data-testid={`store-checkbox-${store.id}`}
                />
                <div>
                  <h3 className="text-lg font-bold mb-1">{store.name}</h3>
                  <p className="text-sm text-[#6b665c] font-mono">{store.location}</p>
                  {store.region && <p className="text-xs text-[#8a8477] font-mono mt-1">{store.region}</p>}
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${
                store.is_online ? 'bg-green-500 animate-pulse-red' : 'bg-zinc-700'
              }`} data-testid={`store-status-${store.id}`} />
            </div>
            <div className="flex items-center gap-2 mb-4 text-xs font-mono text-[#6b665c]" data-testid={`store-hours-${store.id}`}>
              <Clock size={14} className="text-[#8a8477]" />
              <input
                type="time"
                defaultValue={store.open_time || '10:00'}
                onBlur={(e) => e.target.value !== (store.open_time || '10:00') && updateHours(store.id, 'open_time', e.target.value)}
                className="bg-[#f5f0e8] border border-[#c5d4c8] rounded px-1.5 py-0.5 text-xs"
                data-testid={`store-open-time-${store.id}`}
              />
              <span>–</span>
              <input
                type="time"
                defaultValue={store.close_time || '22:00'}
                onBlur={(e) => e.target.value !== (store.close_time || '22:00') && updateHours(store.id, 'close_time', e.target.value)}
                className="bg-[#f5f0e8] border border-[#c5d4c8] rounded px-1.5 py-0.5 text-xs"
                data-testid={`store-close-time-${store.id}`}
              />
              <span className="text-[#a39e92]">open hours</span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => copyKioskUrl(store.id)}
                className="flex-1 bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] text-xs"
                data-testid={`copy-kiosk-url-${store.id}`}
              >
                <Copy size={16} className="mr-2" />
                Copy Kiosk URL
              </Button>
              <Button
                onClick={() => handleDelete(store.id)}
                className="bg-[#fce8eb] hover:bg-[#f0c4cb] text-[#b5636e] border-0"
                data-testid={`delete-store-${store.id}`}
              >
                <Trash size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {stores.length === 0 && (
        <div className="text-center py-12 bg-white border border-[#c5d4c8] rounded-md">
          <p className="text-[#8a8477] font-mono">No stores yet. Add your first store to get started.</p>
        </div>
      )}
    </div>
  );
};

export default Stores;
